import { useState } from 'react';
import { useListFetch, useFetch, useModal } from '../../../../../hooks';
import { getScrapingList, getCampaigns } from '../../../../../api/scraping';
import { getAnalyticsCountries } from '../../../../../api/url-shortener';
import { TaskStatus } from '../../../../../services/scraper';
import type {
  TaskStatusValue,
  TasksDetailsResponse,
} from '../../../../../api/scraping';
import { processStringValue } from '../../../../../utilities';
import type { TaskDetails } from '../../../../../services/scraper';

type CampaignOption = {
  value: string;
  label: string;
};

type CountryOption = {
  value: string;
  label: string;
};

export const useCampaignTasks = () => {
  const {
    isOpened: isModalOpen,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useModal();
  const [selectedTask, setSelectedTask] = useState<TaskDetails | null>(null);
  const { data: campaignOptionsData } = useFetch<CampaignOption[]>(async () => {
    const data = await getCampaigns();
    return (data?.campaigns ?? []).map(name => ({
      value: name,
      label: name,
    }));
  }, []);

  const { data: countryOptionsData } = useFetch<CountryOption[]>(async () => {
    const data = await getAnalyticsCountries();
    return (data?.countries ?? []).map(country => ({
      value: country,
      label: country,
    }));
  }, []);

  const {
    nodes: campaignTasksList = [],
    intializing: loadingCampaignTasks,
    loading: reloadingCampaignTasks,
    refresh,
    filters,
    setFilters,
    pagination,
    setPage,
    ...rest
  } = useListFetch<TasksDetailsResponse>(
    params => {
      const campaignFilter = processStringValue(params?.filters?.campaign_name);

      return getScrapingList(
        (params?.pagination?.page ?? 1) + 1,
        params?.pagination?.perPage ?? 10,
        null,
        campaignFilter || null,
        TaskStatus.SUCCESS_DEMO as TaskStatusValue
      );
    },
    {
      deps: [],
      withQueryParams: true,
    }
  );

  const { tasks, pagination: tasksPagination } =
    (campaignTasksList?.[0] as TasksDetailsResponse | undefined) || {};

  const filteredTasks: TaskDetails[] = (tasks || []).filter(
    (task: TaskDetails) => task.status === TaskStatus.SUCCESS_DEMO
  );

  const handleDetailsClick = (task: TaskDetails) => {
    setSelectedTask(task);
    onOpenModal();
  };

  const handleCloseModal = () => {
    setSelectedTask(null);
    onCloseModal();
  };

  return {
    tasks: filteredTasks,
    pagination: tasksPagination,
    loading: loadingCampaignTasks || reloadingCampaignTasks,
    refresh,
    filters,
    setFilters,
    setPage,
    campaignOptions: campaignOptionsData ?? [],
    countryOptions: countryOptionsData ?? [],
    selectedTask,
    isModalOpen,
    handleDetailsClick,
    handleCloseModal,
    ...rest,
  };
};
